import { useEffect, useRef, useState } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import { getPortraitStatus, regeneratePortrait } from "~/api/portrait"
import { PhotoUploadPanel } from "~/components/PhotoUploadPanel"
import { PageShell } from "~/components/PageShell"
import { LoggedInScreenCard } from "~/components/LoggedInScreenCard"
import { AnimatedProcessingScreen } from "~/components/AnimatedProcessingScreen"

type AddDogPhotoState = {
  sessionToken?: string
  result?: { dog_name?: string }
} | null

const POLL_INTERVAL_MS = 2_000
const MAX_POLL_MS = 90_000

function isPortraitWaitComplete(status: string | null | undefined): boolean {
  return status === "ready" || status === "failed"
}

export function AddDogPhotoPage() {
  const navigate = useNavigate()
  const location = useLocation()
  const state = location.state as AddDogPhotoState
  const dogName = state?.result?.dog_name?.trim() || "your dog"

  const [photo, setPhoto] = useState<File | null>(null)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [waitingForPortrait, setWaitingForPortrait] = useState(false)
  const pollStartedAt = useRef<number | null>(null)

  function continueToResults() {
    const sessionToken = state?.sessionToken
    const path = sessionToken
      ? `/results?token=${encodeURIComponent(sessionToken)}`
      : "/results"
    navigate(path, {
      state: { sessionToken: state?.sessionToken, result: state?.result, addDog: true },
      replace: true,
    })
  }

  useEffect(() => {
    if (!waitingForPortrait) return
    let cancelled = false
    pollStartedAt.current = Date.now()

    async function poll() {
      try {
        const data = await getPortraitStatus()
        if (cancelled) return
        if (isPortraitWaitComplete(data.portrait?.status)) {
          cancelled = true
          continueToResults()
          return
        }
      } catch {
        // Keep waiting; results still work with stock fallback if we time out.
      }
      if (!cancelled && pollStartedAt.current && Date.now() - pollStartedAt.current >= MAX_POLL_MS) {
        cancelled = true
        continueToResults()
      }
    }

    void poll()
    const interval = window.setInterval(() => {
      void poll()
    }, POLL_INTERVAL_MS)
    return () => {
      cancelled = true
      window.clearInterval(interval)
    }
  }, [waitingForPortrait, navigate, state?.sessionToken, state?.result])

  async function handleContinueWithPhoto() {
    if (!photo || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      await regeneratePortrait(photo)
      setWaitingForPortrait(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not start portrait generation")
      setSubmitting(false)
    }
  }

  if (waitingForPortrait) {
    return (
      <AnimatedProcessingScreen
        phrases={[
          `Creating a personalised portrait for ${dogName}…`,
          `Getting ${dogName}'s Doggy Dynamo profile ready…`,
          "Almost there… Adding the finishing touches.",
        ]}
        footerText={`${dogName}'s Doggy Dynamo portrait is almost ready.`}
        phraseIntervalMs={3333}
      />
    )
  }

  return (
    <PageShell innerClassName="max-w-lg w-full" fixedTabNav>
      <LoggedInScreenCard>
      <section className="w-full">
        <h1 className="text-2xl font-semibold text-[var(--color-text-primary)]">
          Add a photo of {dogName}
        </h1>
        <p className="mt-2 text-sm text-[var(--color-text-secondary)]">
          Optional — upload a clear photo so we can create a personalised version of the archetype
          artwork. You can skip and add one later.
        </p>

        <div className="mt-6">
          <PhotoUploadPanel
            dogName={dogName}
            photo={photo}
            previewUrl={previewUrl}
            error={error}
            required
            onPhotoSelected={(file, preview) => {
              setPhoto(file)
              setPreviewUrl(preview)
              setError(null)
            }}
            onClear={() => {
              setPhoto(null)
              setPreviewUrl(null)
            }}
          />
        </div>

        <div className="mt-6 flex flex-col gap-3">
          <button
            type="button"
            disabled={!photo || submitting}
            onClick={() => void handleContinueWithPhoto()}
            className="rounded-lg bg-[var(--color-brand-primary)] px-6 py-3 font-medium text-white hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? "Uploading…" : "Continue with photo"}
          </button>
          <button
            type="button"
            disabled={submitting}
            onClick={continueToResults}
            className="rounded-lg border border-[var(--color-divider)] px-6 py-3 text-sm font-medium text-[var(--color-text-primary)] hover:bg-[var(--color-bg-primary)] disabled:opacity-50"
          >
            Skip for now
          </button>
        </div>
      </section>
      </LoggedInScreenCard>
    </PageShell>
  )
}
